/* eslint-disable no-underscore-dangle */
import 'core-js/stable';
import 'regenerator-runtime/runtime';

import IdleTimer from '@brands/Components/IdleTimer/IdleTimer';
import Toast from '@brands/Components/ToastContainer/ToastContainer';
import { D1NotistackToast, d1NotistackClasses } from '@brands/Components/ToastContainer/D1Toast';
import { config } from '@brands/config/config';
import VideoModal from '@brands/Dashboard/ConsultationPage/Modals/VideoModal/VideoModal';
import { TwilioConversationsClientProvider } from '@brands/hooks/conversation-hooks/TwilioConversationsClientProvider';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { useCustomerIo } from '@brands/hooks/useCustomerIo';
import useScript from '@brands/hooks/useScript';
import { syncGAHeaders } from '@brands/services/client';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectClientBranding } from '@brands/store/selectors/clientBranding';
import { AuthState } from '@brands/store/slices/authSlice';
import { applyClientBranding } from '@brands/Utils/applyClientBranding';
import { SnackbarProvider } from 'notistack';
import React, { FC, useEffect } from 'react';

import { Card } from './assets/types';
import history from './history';
import Routing from './Routes/Routes';

declare global {
  interface Window {
    dataLayer: any[];
    Square: {
      payments: (appId: string, locationId: string) => { card: () => Promise<Card> };
    };
  }
}

const App: FC = () => {
  const userData: AuthState = useAppSelector(selectAuth);
  const clientBranding = useAppSelector(selectClientBranding);
  useCustomerIo(history, userData.userInfo);
  useScript(config.square.src);

  // Single place branding actually gets applied to the DOM, for both the
  // pre-auth /{clientCode} landing flow and the post-auth PrivateRoutes flow.
  // Needed because clientBranding is Redux-Persisted but the CSS custom
  // properties it drives are not re-applied automatically on rehydration.
  //
  // Restricted to patient-facing sessions: Providers/Care Advisors can carry
  // a client tag as an employee too (clientBranding still gets fetched and
  // read directly by staff-facing logo/case-creation UI), but re-skinning
  // the global --secondary_color etc. for their own dashboard corrupted
  // unrelated controls that read that variable, e.g. the schedule editor's
  // time inputs rendering faded.
  const brandingRoleName = userData.userInfo?.role?.name;
  const isPatientFacingSession =
    !userData.userInfo || brandingRoleName === UserRoleName.Patient || brandingRoleName === UserRoleName.Dependent;
  useEffect(() => {
    applyClientBranding(isPatientFacingSession ? clientBranding : null);
  }, [clientBranding, isPatientFacingSession]);

  useEffect(() => {
    const setVh = (): void => {
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    };

    setVh();
    window.addEventListener('resize', setVh);

    return () => {
      window.removeEventListener('resize', setVh);
    };
  }, []);

  useEffect(() => {
    syncGAHeaders();
  }, []);

  useEffect(() => {
    document.body.classList.remove(
      'role-provider',
      'role-care-advisor',
      'role-collector',
      'role-patient',
      'role-admin',
      'role-super-admin',
      'role-organization-admin',
    );

    const roleName = userData.userInfo?.role?.name;

    if (roleName === UserRoleName.Provider) {
      document.body.classList.add('role-provider');
    } else if (roleName === UserRoleName.CareAdvisor) {
      document.body.classList.add('role-care-advisor');
    } else if (roleName === UserRoleName.Collector) {
      document.body.classList.add('role-collector');
    } else if (roleName === UserRoleName.Patient) {
      document.body.classList.add('role-patient');
    } else if (roleName === UserRoleName.Admin) {
      document.body.classList.add('role-admin');
    } else if (roleName === UserRoleName.SuperAdmin) {
      document.body.classList.add('role-super-admin');
    } else if (roleName === UserRoleName.OrganizationAdmin) {
      document.body.classList.add('role-organization-admin');
    }

    return () => {
      document.body.classList.remove(
        'role-provider',
        'role-care-advisor',
        'role-collector',
        'role-patient',
        'role-admin',
        'role-super-admin',
        'role-organization-admin',
      );
    };
  }, [userData.userInfo?.role?.name]);

  useEffect(() => {
    const unlisten = history.listen(({ location }) => {
      const path = location.pathname;

      const isChatPage = path.includes('/messages') || path.includes('/case-chat');

      const isConsultationPage = path.includes('/consultation-page');

      const isSelectProviderPage = path.includes('/select-provider-details') || path.includes('/select-provider');

      const isSelectCAPage = path.includes('/select-care-advisor-details') || path.includes('/select-care-advisor');

      const isSelectPatientPage = path.includes('/select-patient');

      const fullPath = path + location.search + window.location.hash;

      // ===== Chat =====
      if (!isChatPage) {
        sessionStorage.setItem('lastNonMessagesPath', fullPath);
      }

      // ===== Consultation =====
      if (!isConsultationPage && !isChatPage) {
        sessionStorage.setItem('lastNonConsultationPagePath', fullPath);
      }

      // ===== Select Provider =====
      if (!isSelectProviderPage) {
        sessionStorage.setItem('lastNonSelectProviderPagePath', fullPath);
      }

      // ===== Select CA =====
      if (!isSelectCAPage) {
        sessionStorage.setItem('lastNonSelectCAPagePath', fullPath);
      }

      // ===== Select Patient =====
      // Exclude the pages that SelectPatient navigates forward to so that
      // transitioning into the intake flow does not overwrite the back path.
      if (!isSelectPatientPage && !isSelectCAPage && !isSelectProviderPage) {
        sessionStorage.setItem('lastNonSelectPatientPagePath', fullPath);
      }
    });

    return () => {
      unlisten();
    };
  }, []);

  return (
    <>
      <SnackbarProvider
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        classes={d1NotistackClasses}
        Components={{
          default: D1NotistackToast,
          error: D1NotistackToast,
          info: D1NotistackToast,
          success: D1NotistackToast,
          warning: D1NotistackToast,
        }}
        maxSnack={3}
        preventDuplicate
      >
        {/*
          DO-3975: owns the app-wide Twilio Conversations Client
          singleton. Wrapping `<Routing />` means every consumer of
          `useTwilioConversationsClient` (the chat window's
          useConversations / useTwilioConversationBySid, Sidenav's
          useConversationStatisticsRealtime) shares one Client and
          one WebSocket instead of each opening their own.
        */}
        <TwilioConversationsClientProvider>
          <IdleTimer />
          {userData.userInfo?.role.name !== UserRoleName.Patient && <VideoModal />}
          <Routing />
        </TwilioConversationsClientProvider>
      </SnackbarProvider>
      <Toast />
    </>
  );
};
export default App;
